<template>
  <DetailCard :dt="null">
    <template #heading>
      <span class="pi pi-bolt"></span>
      <a v-if="ehrId" :href="getEhrCommunalExternalUrl" target="_blank">
        {{ $t(`messages.detail.ehrCommunal.title`) }}
      </a>
      <span v-else>{{ $t(`messages.detail.ehrCommunal.title`) }}</span>
    </template>
    <div v-for="field in inputFields" :key="field" class="grid grid-cols-2 gap-4 mb-4">
      <label :for="field" class="font-bold">
        {{ $t(`messages.detail.ehrCommunal.${field}`) }}
      </label>
      <PredefinedValuesInputComponent
        v-if="predefined.includes(field)"
        category="ehrCommunal"
        :name="field"
        :append-on-select="true"
        v-model="ehrCommunal[field]"
        @update:model-value="evaluationStore.save({ communal: { [field]: $event } })"
      />
      <InputText v-else :id="field" v-model="ehrCommunal[field]" fluid @update:model-value="evaluationStore.save({ communal: { [field]: $event } })" />
    </div>
  </DetailCard>
</template>
<script setup>
import { storeToRefs } from 'pinia'
import { computed, readonly, shallowRef, watch } from 'vue'
import PredefinedValuesInputComponent from '@/components/PredefinedValuesInputComponent.vue'
import { useDetailStore } from '@/stores/useDetailStore'
import { useEvaluationStore } from '@/stores/useEvaluationStore'
import DetailCard from '../DetailCard.vue'

const props = defineProps({
  // Land mode enables the EHR auto-prefill below; built evaluations get the
  // same data through the main building's xRoadEhr hydration in useDetailStore.
  isLandParcel: { type: Boolean, default: false },
})

const store = useDetailStore()
const evaluationStore = useEvaluationStore()

const { ehrCommunal, ehrId } = storeToRefs(store)

const inputFields = readonly([
  'elektrisysteemi_liik',
  'veevarustuse_liik',
  'kanalisatsiooni_liik',
  'soojusvarustuse_liik',
  'soojusallikas',
  'energiaallikas',
  'ventilatsiooni_liik',
  'jahutussysteemi_liik',
  'gaasi_olemasolu',
  'side',
  'tehnovarustus_markused',
  'tehnovarustus_seisuskord',
])
const predefined = readonly([
  'side',
  'tehnovarustus_markused',
  'tehnovarustus_seisuskord',
])

const getEhrCommunalExternalUrl = computed(() => {
  return 'https://ehr.ee/app/link/b/' + ehrId.value + '/tech'
})

// Maatükk (RED-268): a planned hoone on the parcel already declares its
// utilities in EHR — auto-prefill the still-empty fields from it, silently,
// once, as soon as the Hooned ja rajatised list resolves. Never overwrites
// the valuer. Land-only by prop: built evaluations already hydrate these
// fields from the main building's xRoadEhr payload.
const prefillHoone = computed(() =>
  props.isLandParcel
    ? (store.buildings || []).find(b => b.type === 'Hoone' && b.ehrCode)
    : null,
)

const prefillDone = shallowRef(false)

const fillFromEhr = async hoone => {
  try {
    const { data } = await axios.get('/xroad/ehr/' + hoone.ehrCode)
    const communal = data?.communal ?? {}

    const filled = {}
    for (const field of inputFields) {
      const value = communal[field]
      if (value && value !== '-' && !ehrCommunal.value[field]) {
        ehrCommunal.value[field] = value
        filled[field] = value
      }
    }

    if (Object.keys(filled).length) {
      evaluationStore.save({ communal: filled })
    }
  } catch {
    // Prefill is best-effort sugar — the fields stay manually editable.
  }
}

watch(
  prefillHoone,
  hoone => {
    if (hoone && !prefillDone.value) {
      prefillDone.value = true
      fillFromEhr(hoone)
    }
  },
  { immediate: true },
)
</script>
